import type { LegacyDashboardFilters, BooleanFilter } from '../models/Filters';
import type { LegacyDashboardClient } from '../models/LegacyDashboardClient';

function matchesBooleanFilter(value: boolean | null | undefined, filter: BooleanFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'yes') return value === true;
  return value !== true;
}

export function matchesSearch(client: LegacyDashboardClient, term: string): boolean {
  if (!term) return true;
  const haystack = [
    String(client.legacy_team_id),
    client.name,
    client.admin?.name,
    client.admin?.email,
    client.admin?.phone,
    client.new_child_team_name,
    client.country_code,
    ...(client.shop_channels ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(term.toLowerCase());
}

export function applyLegacyDashboardFilters(
  clients: LegacyDashboardClient[],
  filters: LegacyDashboardFilters,
  searchTerm: string,
): LegacyDashboardClient[] {
  return clients.filter((client) => {
    if (filters.migrationFilter === 'migrated' && !client.is_migrated) return false;
    if (filters.migrationFilter === 'not_migrated' && client.is_migrated) return false;

    if (filters.paymentProfiles.length > 0 && !filters.paymentProfiles.includes(client.payment_profile)) {
      return false;
    }

    if (filters.shopChannels.length > 0) {
      const channels = client.shop_channels ?? [];
      if (!filters.shopChannels.some((channel) => channels.includes(channel))) return false;
    }

    if (filters.countryCodes.length > 0) {
      if (!client.country_code || !filters.countryCodes.includes(client.country_code)) return false;
    }

    if (filters.migrationRunStatuses.length > 0) {
      if (!client.migration_run_status || !filters.migrationRunStatuses.includes(client.migration_run_status)) {
        return false;
      }
    }

    if (!matchesBooleanFilter(client.verified, filters.verified)) return false;
    if (!matchesBooleanFilter(client.is_dropshipping, filters.isDropshipping)) return false;
    if (!matchesBooleanFilter(client.crm_active, filters.crmActive)) return false;

    if (filters.minOrdersCount != null && client.orders_count < filters.minOrdersCount) return false;
    if (filters.maxOrdersCount != null && client.orders_count > filters.maxOrdersCount) return false;
    if (filters.minAvgOrdersPerMonth != null && client.avg_orders_per_month < filters.minAvgOrdersPerMonth) {
      return false;
    }
    if (filters.maxAvgOrdersPerMonth != null && client.avg_orders_per_month > filters.maxAvgOrdersPerMonth) {
      return false;
    }

    return matchesSearch(client, searchTerm);
  });
}

export function countActiveLegacyDashboardFilters(filters: LegacyDashboardFilters): number {
  let count = 0;
  if (filters.migrationFilter !== 'all') count++;
  if (filters.paymentProfiles.length > 0) count++;
  if (filters.shopChannels.length > 0) count++;
  if (filters.countryCodes.length > 0) count++;
  if (filters.migrationRunStatuses.length > 0) count++;
  if (filters.verified !== 'all') count++;
  if (filters.isDropshipping !== 'all') count++;
  if (filters.crmActive !== 'all') count++;
  if (filters.minOrdersCount != null) count++;
  if (filters.maxOrdersCount != null) count++;
  if (filters.minAvgOrdersPerMonth != null) count++;
  if (filters.maxAvgOrdersPerMonth != null) count++;
  return count;
}
